<template>
  <div
    class="kpi-card border rounded p-2 d-flex flex-column justify-content-center cursor-pointer"
    :class="{ 'kpi-active': ativo, 'kpi-loading': loading, 'kpi-clickable': clicavel }"
    @click="clicavel && $emit('click')"
    :title="tooltip"
  >
    <div class="d-flex align-items-center gap-1 mb-1">
      <i :class="icone" class="small kpi-icon"></i>
      <span class="small kpi-rotulo text-truncate">{{ rotulo }}</span>
      <span v-if="badge" class="badge kpi-badge ms-auto">{{ badge }}</span>
    </div>
    <div v-if="!loading" class="kpi-valor fw-bold">{{ valorFormatado }}</div>
    <div v-else class="kpi-shimmer"></div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  valor: { type: [Number, String], default: 0 },
  rotulo: { type: String, required: true },
  icone: { type: String, default: 'bi bi-graph-up' },
  formato: { type: String, default: 'numero' },
  clicavel: { type: Boolean, default: false },
  ativo: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  badge: { type: String, default: null },
  tooltip: { type: String, default: '' }
})

defineEmits(['click'])

const valorFormatado = computed(() => {
  if (props.valor == null || props.valor === '') return '-'
  const num = typeof props.valor === 'string' ? parseFloat(props.valor) : props.valor
  if (isNaN(num)) return props.valor

  switch (props.formato) {
    case 'moeda':
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(num)
    case 'moeda-usd':
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num)
    case 'compacto':
      if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M'
      if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K'
      return num.toFixed(0)
    case 'decimal':
      return num.toFixed(4)
    default:
      return num.toLocaleString('pt-BR')
  }
})
</script>

<style scoped>
.kpi-card {
  --kpi-border: var(--bs-border-color);
  --kpi-bg: var(--bs-body-bg);
  --kpi-text: var(--bs-body-color);
  --kpi-text-muted: var(--bs-secondary-color);
  --kpi-active-bg: #f0f6ff;
  --kpi-active-border: var(--bs-primary);
  --kpi-badge-bg: var(--bs-warning-bg-subtle);
  --kpi-badge-color: var(--bs-warning-text-emphasis);
  min-width: 110px;
  transition: all 0.2s ease;
  border-color: var(--kpi-border) !important;
  background: var(--kpi-bg);
}

[data-bs-theme="dark"] .kpi-card {
  --kpi-active-bg: #0a2a4a;
}

.kpi-card:hover {
  border-color: var(--bs-secondary-color) !important;
}

.kpi-clickable { cursor: pointer; }

.kpi-clickable:hover {
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
}

.kpi-active {
  border-color: var(--kpi-active-border) !important;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.2);
  background: var(--kpi-active-bg);
}

.kpi-icon { color: var(--kpi-text-muted); }
.kpi-rotulo { color: var(--kpi-text-muted); }

.kpi-valor {
  font-size: 1.1rem;
  line-height: 1.2;
  color: var(--kpi-text);
}

.kpi-badge {
  background: var(--kpi-badge-bg);
  color: var(--kpi-badge-color);
  font-size: 0.6rem;
}

.kpi-shimmer {
  height: 22px;
  background: linear-gradient(90deg, var(--bs-tertiary-bg) 25%, var(--bs-secondary-bg) 50%, var(--bs-tertiary-bg) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
